import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function ArthroscopyPage() {
  const benefits = [
    "Smaller incisions and minimal scarring",
    "Less post-operative pain",
    "Faster recovery and rehabilitation",
    "Day-care or short-stay procedure",
    "Early mobilization with physiotherapy",
    "Reduced risk of complications",
  ];

  const conditions = [
    {
      title: "Knee Arthroscopy",
      items: ["ACL reconstruction", "Meniscus repair or removal", "Cartilage procedures", "Loose body removal"],
    },
    {
      title: "Shoulder Arthroscopy",
      items: ["Rotator cuff repair", "Labral tear repair", "Shoulder instability surgery", "Subacromial decompression"],
    },
  ];

  const faqs = [
    {
      question: "How soon can I walk after knee arthroscopy?",
      answer:
        "Most patients can walk with support on the same day or next day after surgery. Full weight-bearing typically begins within a few days, depending on the specific procedure performed.",
    },
    {
      question: "Will I need a brace after surgery?",
      answer:
        "This depends on the procedure. ACL reconstruction typically requires a brace for 4-6 weeks, while simple meniscus procedures may not need one. Your surgeon will provide specific guidance.",
    },
    {
      question: "When can I drive after arthroscopy?",
      answer:
        "You can usually drive once you're off pain medications and have regained good control of the operated limb, typically 1-2 weeks for upper limb and 2-4 weeks for lower limb procedures.",
    },
    {
      question: "What is the timeline for return to sport?",
      answer:
        "Return to sport varies by procedure and sport type. Simple procedures may allow return in 6-8 weeks, while ACL reconstruction typically requires 6-9 months. We provide individualized return-to-sport protocols.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4 font-[family-name:var(--font-playfair)]">
              Arthroscopy
            </h1>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              Minimally invasive joint surgery for faster recovery and better outcomes
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-start">
            <div>
              <h2 className="text-3xl font-bold mb-6 font-[family-name:var(--font-playfair)]">What is Arthroscopy?</h2>
              <div className="prose prose-lg max-w-none space-y-4">
                <p className="text-foreground leading-relaxed">
                  Arthroscopy uses a small camera and keyhole instruments to diagnose and treat joint problems with
                  minimal tissue damage. Common indications include ACL and meniscus tears in the knee, and rotator cuff
                  or labral injuries in the shoulder.
                </p>
                <p className="text-foreground leading-relaxed">
                  Benefits often include smaller scars, less pain, and quicker rehabilitation when compared to open
                  surgery. The procedure is typically performed as a day-care or short-stay surgery, allowing you to
                  return home the same day or next day.
                </p>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Benefits of Arthroscopy</h3>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="/arthroscopy-knee-surgery-minimally-invasive-proce.jpg"
                alt="Arthroscopic surgery procedure"
                className="object-cover w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 font-[family-name:var(--font-playfair)]">
            Conditions We Treat
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            {conditions.map((condition) => (
              <Card key={condition.title}>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4">{condition.title}</h3>
                  <ul className="space-y-2">
                    {condition.items.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 font-[family-name:var(--font-playfair)]">
            What to Expect
          </h2>
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Before Surgery</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You'll have a pre-operative consultation where we'll review your imaging, discuss the procedure in
                  detail, and answer all your questions. We'll explain the expected timeline for recovery and return to
                  activities.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">During Surgery</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The procedure is performed under anesthesia. Small incisions (typically 5-10mm) are made to insert the
                  camera and instruments. The surgery duration varies from 30 minutes to 2 hours depending on the
                  complexity.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">After Surgery</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Early mobilization begins with physiotherapy-led rehabilitation. You'll receive a detailed recovery
                  plan with timelines for return to routine activities, driving, and sport. Regular follow-ups ensure
                  optimal healing.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 font-[family-name:var(--font-playfair)]">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 font-[family-name:var(--font-playfair)]">
            Ready to Discuss Your Treatment?
          </h2>
          <p className="text-lg mb-8 text-muted-foreground max-w-2xl mx-auto">
            Schedule a consultation to learn if arthroscopy is right for your condition.
          </p>
          <Button size="lg" asChild>
            <Link href="/book-appointment">
              Book Appointment
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
